import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { formatCPF } from '@/utils/cpf';
import { LanguageSelector } from '@/components/LanguageSelector';
import { detectLanguageByIP } from '@/i18n';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Detectar idioma por IP na primeira visita
  useEffect(() => {
    detectLanguageByIP();
  }, []);

  const handleLoginIdentifierChange = (value) => {
    if (/^[\d.\-\s]*$/.test(value)) {
      setUsername(formatCPF(value));
      return;
    }
    setUsername(value.toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(username.trim(), password);
    } catch (err) {
      // Handle specific error messages from backend
      const errorMsg = err.response?.data?.detail || t('invalidCredentials');
      
      if (errorMsg.includes('aguardando aprovação')) {
        setError(t('accountPendingApproval'));
      } else if (errorMsg.includes('não foi aprovado')) {
        setError(t('accountRejected'));
      } else if (errorMsg.includes('desativada')) {
        setError(t('accountDisabled'));
      } else {
        setError(t('invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      // Always show success to prevent email enumeration
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotEmail('');
    setForgotSent(false);
  };

  return (
    <div 
      className="relative flex min-h-[100dvh] items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(2, 6, 23, 0.75), rgba(2, 6, 23, 0.9)), url('/assets/octopus-background.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector variant="flags-only" />
      </div>

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-primary/5 rounded-full blur-2xl" />
      </div>

      <Card className="w-full max-w-md glass border-primary/20 relative z-10">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-primary/30 to-primary/10 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20">
            <img 
              src="/assets/octopus-icon.png" 
              alt="Octopus" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold tracking-tight text-foreground">
              Octopus
            </CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              {t('loginSubtitle')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-foreground">CPF, TAG do equipamento ou usuário</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => handleLoginIdentifierChange(e.target.value)}
                placeholder="000.000.000-00, DRA-0006 ou usuário"
                maxLength={32}
                className="bg-secondary/50 border-border focus:border-primary"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">{t('password')}</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-secondary/50 border-border focus:border-primary"
                required
              />
            </div>

            {error && (
              <div className="text-destructive text-sm text-center bg-destructive/10 p-3 rounded-md" data-testid="login-error">
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-[0_0_15px_rgba(14,165,233,0.3)] hover:shadow-[0_0_25px_rgba(14,165,233,0.5)] transition-all"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {t('login')}
            </Button>

            <Button
              type="button"
              variant="link"
              className="w-full text-muted-foreground hover:text-primary"
              onClick={() => setForgotPasswordOpen(true)}
              data-testid="forgot-password-btn"
            >
              {t('forgotPassword')}
            </Button>
            
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{t('or')}</span>
              </div>
            </div>
            
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => window.location.href = '/signup'}
              data-testid="signup-btn"
            >
              {t('createAccount')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={closeForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              {t('forgotPassword')}
            </DialogTitle>
            <DialogDescription>
              {forgotSent 
                ? t('resetEmailSentDescription')
                : t('forgotPasswordDescription')}
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="flex flex-col items-center py-6 space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-center text-muted-foreground">
                {t('resetEmailSent')}
              </p>
              <Button onClick={closeForgotPassword} className="w-full">
                {t('backToLogin')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t('email')}</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForgotPassword}
                  className="w-full sm:w-auto"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full sm:w-auto"
                  data-testid="send-reset-email-btn"
                >
                  {forgotLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {t('sendResetEmail')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
